import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { MarketStatusBar } from '../components/MarketStatusBar';
import { AgentActivityFeed } from '../components/AgentActivityFeed';
import { AlertsStream } from '../components/AlertsStream';
import { toast } from 'sonner';
import {
  RefreshCw, Zap, TrendingUp, ShoppingCart, Clock, BarChart3,
  Target, ShieldAlert, ArrowRight, AlertTriangle, Timer, Briefcase
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function TimeAgo({ date }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);
  if (!date) return null;
  const diff = Math.floor((now - new Date(date).getTime()) / 1000);
  if (diff < 60) return <span>{diff}s ago</span>;
  if (diff < 3600) return <span>{Math.floor(diff/60)}m ago</span>;
  if (diff < 86400) return <span>{Math.floor(diff/3600)}h ago</span>;
  return <span>{Math.floor(diff/86400)}d ago</span>;
}

function StockTile({ rec, onRefresh }) {
  const navigate = useNavigate();
  const isActive = rec.status === 'active';
  const isBought = rec.status === 'bought';
  const isExpired = rec.status === 'expired';

  const estReturn = rec.estimated_return_pct || (
    rec.buy_price > 0 ? ((rec.target_price - rec.buy_price) / rec.buy_price * 100) : 0
  );
  const successPct = rec.confidence || 0;

  const handleBuy = async (e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/api/recommendations/${rec._id}/buy`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: 1 }),
      });
      if (res.ok) { toast.success(`Bought ${rec.symbol}`); if (onRefresh) onRefresh(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
  };

  const borderColor = isActive ? 'border-[hsl(var(--primary))]/40' : isBought ? 'border-emerald-500/40' : 'border-[hsl(var(--border))]';

  return (
    <div
      onClick={() => navigate(`/recommendation/${rec._id}`)}
      className={`relative p-4 rounded-xl border ${borderColor} bg-[hsl(var(--card))] hover:border-[hsl(var(--hairline))] hover:shadow-[0_18px_40px_-20px_hsl(var(--shadow-raise))] transition-all duration-200 cursor-pointer group`}
      data-testid="stock-tile"
    >
      {/* Top row: Symbol + Status + Timer */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-mono text-lg font-bold">{rec.symbol}</span>
          <Badge variant="outline" className={`text-[10px] uppercase font-semibold ${
            isActive ? 'text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30' :
            isBought ? 'text-emerald-400 border-emerald-500/30' :
            'text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]'
          }`}>{rec.status}</Badge>
        </div>
        <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
          <Timer className="w-3 h-3" />
          <TimeAgo date={rec.created_at} />
        </div>
      </div>

      {/* Company name */}
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3 truncate">{rec.company_name}</p>

      {/* Price row - simplified */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-1 text-center p-2 rounded-lg bg-[hsl(var(--surface-2))]">
          <p className="text-[10px] text-[hsl(var(--muted-foreground))] mb-0.5">Buy</p>
          <p className="font-mono text-sm font-semibold tabular-nums">${rec.buy_price?.toFixed(2)}</p>
        </div>
        <div className="flex-1 text-center p-2 rounded-lg bg-[hsl(var(--surface-2))]">
          <p className="text-[10px] text-emerald-400 mb-0.5">Target</p>
          <p className="font-mono text-sm font-semibold tabular-nums text-emerald-400">${rec.target_price?.toFixed(2)}</p>
        </div>
        <div className="flex-1 text-center p-2 rounded-lg bg-[hsl(var(--surface-2))]">
          <p className="text-[10px] text-red-400 mb-0.5">Stop</p>
          <p className="font-mono text-sm font-semibold tabular-nums text-red-400">${rec.stop_loss?.toFixed(2)}</p>
        </div>
      </div>

      {/* Success + Risk + Return in one row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1">
          <div className={`w-2 h-2 rounded-full ${successPct >= 70 ? 'bg-emerald-400' : successPct >= 50 ? 'bg-[hsl(var(--primary))]' : 'bg-red-400'}`} />
          <span className="font-mono text-xs tabular-nums font-medium">{successPct}%</span>
        </div>
        <Badge variant="outline" className={`text-[10px] ${
          rec.risk_level === 'LOW' ? 'text-emerald-400 border-emerald-500/30' :
          rec.risk_level === 'HIGH' ? 'text-red-400 border-red-500/30' :
          'text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30'
        }`}>
          <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />{rec.risk_level}
        </Badge>
        <span className="font-mono text-sm font-bold tabular-nums text-emerald-400">+{Number(estReturn).toFixed(1)}%</span>
      </div>

      {/* Expiry info */}
      {isActive && (
        <p className="text-[10px] text-[hsl(var(--muted-foreground))] mb-3">
          Expires when price exceeds ${rec.buy_price?.toFixed(2)} {rec.timeframe && `| Timeframe: ${rec.timeframe}`}
        </p>
      )}
      {isExpired && rec.expired_at && (
        <p className="text-[10px] text-red-400/70 mb-3">
          Expired: {new Date(rec.expired_at).toLocaleString()}
        </p>
      )}

      {/* Action */}
      <div className="flex items-center gap-2">
        {isActive && (
          <Button size="sm" onClick={handleBuy}
            className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 text-xs h-8 px-3"
            data-testid="tile-buy-button">
            <ShoppingCart className="w-3 h-3 mr-1" />Buy
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/recommendation/${rec._id}`); }}
          className="text-[hsl(var(--muted-foreground))] text-xs h-8 px-3" data-testid="tile-view-button">
          Details <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [filter, setFilter] = useState({ minSuccess: 0, risk: 'ALL' });

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/dashboard/summary`);
      if (res.ok) setData(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 15000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const triggerAnalysis = async () => {
    setTriggering(true);
    try {
      const res = await fetch(`${API}/api/analysis/trigger`, { method: 'POST' });
      if (res.ok) toast.success('Analysis triggered!');
    } catch { toast.error('Network error'); }
    finally { setTriggering(false); }
  };

  const markAlertsRead = async () => {
    try { await fetch(`${API}/api/alerts/mark-read`, { method: 'POST' }); fetchDashboard(); } catch {}
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full rounded-xl bg-[hsl(var(--surface-2))]" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-52 rounded-xl bg-[hsl(var(--surface-2))]" />)}
        </div>
      </div>
    );
  }

  const recommendations = (data?.recommendations || []).filter(rec => {
    if (filter.minSuccess > 0 && (rec.confidence || 0) < filter.minSuccess) return false;
    if (filter.risk !== 'ALL' && rec.risk_level !== filter.risk) return false;
    return true;
  });
  const positions = data?.positions || [];
  const alerts = data?.alerts || [];
  const stats = data?.stats || {};
  const market = data?.market || {};
  const agents = data?.agents || {};

  return (
    <div className="space-y-5 animate-fade-in" data-testid="dashboard-page">
      <MarketStatusBar market={market} isAnalyzing={agents.is_analyzing} lastAnalysis={agents.last_analysis} />

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Zap} label="Recommendations" value={stats.total_recommendations || 0} color="primary" />
        <StatCard icon={ShoppingCart} label="Bought" value={stats.total_bought || 0} color="success" />
        <StatCard icon={TrendingUp} label="Active Positions" value={stats.active_positions || 0} color="info" />
        <StatCard icon={Clock} label="Expired" value={stats.total_expired || 0} color="muted" />
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-4">
          {/* Header + Filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[hsl(var(--primary))]" />Today's Picks
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Risk Filter */}
              <select value={filter.risk} onChange={(e) => setFilter(f => ({...f, risk: e.target.value}))}
                className="text-xs px-3 py-1.5 rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))]" data-testid="filter-risk">
                <option value="ALL">All Risk</option>
                <option value="LOW">Low Risk</option>
                <option value="MEDIUM">Medium Risk</option>
                <option value="HIGH">High Risk</option>
              </select>
              {/* Success Filter */}
              <select value={filter.minSuccess} onChange={(e) => setFilter(f => ({...f, minSuccess: Number(e.target.value)}))}
                className="text-xs px-3 py-1.5 rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))]" data-testid="filter-success">
                <option value={0}>All Success %</option>
                <option value={50}>50%+</option>
                <option value={60}>60%+</option>
                <option value={70}>70%+</option>
                <option value={80}>80%+</option>
              </select>
              <Button variant="outline" size="sm" onClick={triggerAnalysis} disabled={triggering || agents.is_analyzing}
                className="border-[hsl(var(--border))] text-xs h-8" data-testid="trigger-analysis-button">
                <RefreshCw className={`w-3 h-3 mr-1 ${(triggering || agents.is_analyzing) ? 'animate-spin' : ''}`} />
                {agents.is_analyzing ? 'Analyzing...' : 'Run Analysis'}
              </Button>
            </div>
          </div>

          {/* Stock Tiles Grid */}
          {recommendations.length === 0 ? (
            <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-10 text-center" data-testid="dashboard-today-recommendations-section">
              <BarChart3 className="w-10 h-10 mx-auto mb-3 text-[hsl(var(--muted-foreground))] opacity-30" />
              <h3 className="text-base font-medium mb-1">No recommendations yet</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
                {agents.is_analyzing ? 'Agents are scanning...' : 'Click Run Analysis to start.'}
              </p>
              {!agents.is_analyzing && (
                <Button onClick={triggerAnalysis} size="sm" className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                  <Zap className="w-3 h-3 mr-1" />Generate
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="dashboard-today-recommendations-section">
              {recommendations.map((rec) => <StockTile key={rec._id} rec={rec} onRefresh={fetchDashboard} />)}
            </div>
          )}

          {/* Positions */}
          {positions.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-2">
                <Briefcase className="w-4 h-4" />Active Positions
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {positions.map(pos => (
                  <div key={pos._id} className="p-3 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--border))]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-sm font-bold">{pos.symbol}</span>
                      <span className={`font-mono text-sm font-semibold tabular-nums ${(pos.pnl_pct || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {(pos.pnl_pct || 0) >= 0 ? '+' : ''}{(pos.pnl_pct || 0).toFixed(2)}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                      <span>Entry: ${pos.buy_price?.toFixed(2)}</span>
                      <span className="text-emerald-400">T: ${pos.target_price?.toFixed(2)}</span>
                      <span className="text-red-400">S: ${pos.stop_loss?.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Rail */}
        <div className="lg:col-span-4 space-y-4">
          <AgentActivityFeed agents={agents} isAnalyzing={agents.is_analyzing} lastAnalysis={agents.last_analysis} />
          <AlertsStream alerts={alerts} unreadCount={data?.unread_alerts || 0} onMarkRead={markAlertsRead} />
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = { primary: 'text-[hsl(var(--primary))]', success: 'text-emerald-400', info: 'text-[hsl(var(--info))]', muted: 'text-[hsl(var(--muted-foreground))]' };
  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-3.5 h-3.5 ${colors[color]}`} />
        <span className="text-[10px] text-[hsl(var(--muted-foreground))] font-medium uppercase tracking-wider">{label}</span>
      </div>
      <p className="font-mono text-xl font-bold tabular-nums">{value}</p>
    </div>
  );
}
